import { useRouter } from "next/router";
import { createMarkup } from "../../../utils/helper";
import { Event } from "../../../context/eventsContext/reducer";
import EventDate from "./elements/EventDate";
import EventLocation from "./elements/EventLocation";
import RelatedEvents from "./collections/RelatedEvents";
import Button from "../Buttons/Button";
import { formatTitle } from '../../../utils/helper';
import Link from "next/link";
import Head from "next/head";
import { useToast } from "../../../context/toastContext";
import { formatIsoDateToLocalString } from "../../../utils/date";

interface IEventDetailProps {
  event?: Event;
  hasPast?: boolean;
  error?: string;
}

const EventDetail = ({ event, hasPast, error }: IEventDetailProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const toast = useToast();

  if (error) {
    toast("error", error);
    return null;
  }

  const formattedDate = event?.date
    ? formatIsoDateToLocalString(event.date)
    : "";
  const metaDescription = hasPast
    ? `${event.title} took place on ${formattedDate}. See what the event was about`
    : `Join us for ${event.title} happening on ${formattedDate}. Learn more and register.`;

  return (
    <div className="flex flex-col lg:flex-row lg:space-x-8 py-4">
      <Head>
        <title>{formatTitle(event.title)}</title>
        <meta
          name="description"
          content={
            typeof event?.preview === "string" && event?.preview.length > 0
              ? event.preview
              : metaDescription
          }
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://vinous.com/events/${slug}`} />
        <meta
          name="google-translate-customization"
          content="bc9c404857e6094b-bd2091ef6a1aacc6-g5523c9461670c8e8-21"
        />
      </Head>
      <div className="w-full lg:w-3/4">
        {
          hasPast &&
          <div className="bg-red w-full p-8 rounded flex items-center justify-center mb-4">
            <div className="text-center">
              <div className="text-2xl text-white">
                This event has passed but there will always be more.
              </div>
              <div className="font-light text-white">
                We can&#39;t wait to see you at the next event!
              </div>
              <Button 
                type="secondary"
                label="View Upcoming Events"
                className="mt-4 w-64 mx-auto bg-white text-red hover:shadow-lg"
                onClick={() => {
                  router.push('/events');
                }}
              />
            </div>
          </div>
        }
        <div 
          className="event-content font-light" 
          dangerouslySetInnerHTML={createMarkup(event.description)}
        >
        </div>
      </div>
      <div className="w-full lg:w-1/4">
        <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4">
          Event Information
        </div>
        <div className="px-2">
          <div className="text-sm">
            Name
          </div>
          <p
            className="font-light pb-4"
          >
            { event.title }
          </p>

          {
            event.preview &&
            <div>
              <div className="text-sm">
                Description
              </div>
              <p
                className="font-light pb-4"
              >
                { event.preview }
              </p>
            </div>
          }

          <EventDate 
            event={event}
          />

          <EventLocation 
            event={event}
          />
        </div>

        {
          event.ticketable && event.ticket_types.length > 0 &&
          <div>
            <div className="font-light text-xl border-b border-gray-100 pb-2 mb-4 mt-8">
              {
                hasPast 
                ? "Tickets (No Longer Available)"
                : "Tickets"
              }
            </div>

            <div className="space-y-2">
            {event.ticket_types
                .sort((a, b) => {
                  if (a.display_order === null) return 1;
                  if (b.display_order === null) return -1;
                  return a.display_order - b.display_order;
                })
                .map((ticket) => (
                  <Link key={ticket.id} href={`/tickets/${event.slug}`}>
                    <div
                      className={`transition p-4 bg-gray-50 rounded ${
                        hasPast
                          ? "cursor-not-allowed opacity-40"
                          : "cursor-pointer hover:shadow-md"
                      }`}
                    >
                      <div className="leading-5 pb-2">
                        {ticket.name}
                      </div>
                      <div className="flex w-full opacity-50">
                        <div className="w-full">
                          {"Max " + ticket.allowed_per_person}{" "}
                          {ticket.allowed_per_person > 1
                            ? "tickets/person"
                            : "ticket"}
                        </div>
                        <div className="self-end">
                          {ticket.price_in_cents === 0
                            ? "Free"
                            : "$" + ticket.price_in_cents / 100 + "/ticket"}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        }

        <RelatedEvents 
          parentEvent={event}
        />
      </div>
    </div>
  )
}

export default EventDetail;
